import { useAuth } from '@/hooks/useAuth';
import { NavItem, NavVisibility } from '@/config/navigation';

/**
 * Hook to determine navigation item visibility based on user roles.
 * 
 * Visibility hierarchy:
 * - super_admin: Only Super Admins
 * - firm_owner: Super Admins OR Firm Owners (has org membership)
 * - business_admin: Super Admins OR Firm Owners OR Business Admins
 * - staff: All authenticated users with business membership
 * - all: All authenticated users
 */
export function useNavVisibility() {
  const { isSuperAdmin, isOrgAdmin, activeBusiness } = useAuth();

  /**
   * Check if current user can see a nav item based on visibility level
   */
  const canSee = (visibility: NavVisibility): boolean => {
    // Super Admins ONLY see super_admin and utility items
    // They do NOT see firm_owner, business_admin, or staff items
    if (isSuperAdmin) {
      return visibility === 'super_admin' || visibility === 'utility';
    }

    switch (visibility) {
      case 'utility':
        // Universal items shown to all authenticated users
        return true;
      
      case 'super_admin':
        return false; // Already handled above
      
      case 'firm_owner':
        return isOrgAdmin;
      
      case 'business_admin':
        return isOrgAdmin || activeBusiness?.role === 'firm_owner';
      
      case 'staff':
        // All business members can see staff-level items
        return true;
      
      default:
        return true;
    }
  };

  /**
   * Filter an array of nav items based on user visibility
   */
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return items.filter(item => canSee(item.visibility));
  };

  /**
   * Check if user can see the Platform section (Super Admin only)
   */
  const canSeePlatformSection = isSuperAdmin;

  return {
    canSee,
    filterNavItems,
    canSeePlatformSection,
  };
}
